import {
  WorldManifest,
  type Aabb,
  type Billboard,
  type CommercialTemplateId,
  type District,
  type InteriorTemplate,
  type Plot,
  type StaticCollider,
  type Surface,
  type WorldProp,
} from "@townsy/contracts";
import { HILLCREST } from "./districts/hillcrest";
import { RIVERSIDE } from "./districts/riverside";
import { LINK_CORRIDOR_Z, shoppingDistrict, type DistrictBuild } from "./districts/shoppingDistrict";
import { ARENA_AVENUE_CORRIDOR_X, arenaAvenueInCentral, sportsPark } from "./districts/sportsPark";
import { YAW_EAST, YAW_NORTH, YAW_SOUTH, YAW_WEST, aabb } from "./geometry";
import {
  COMMERCIAL_FRONT_Z,
  COMMERCIAL_PITCH,
  ENTRANCE_SETBACK,
  PLOT_D,
  ROAD_HALF,
  SIDEWALK,
  SIDE_ROAD_HALF,
  commercialPlot,
  propColliders,
  residentialPlot,
} from "./plots";
import { CIVIC_OFFICE, STOREFRONT_SIGN_Y } from "./templates";
import {
  ARENA_INTERIORS,
  ASPEN_AVENUE_X,
  MARKET_NORTH_FRONT_Z,
  MARKET_STREET_X_MIN,
  MARKET_STREET_Z,
  STADIUM_SITES,
  VENUE_SITES,
  WILLOW_AVENUE_X,
  casinoTemplate,
  raceTrackTemplate,
  sportsCenterTemplate,
  truckArenaTemplate,
  venuePlots,
} from "./venues";

/**
 * World manifest v4 — "Townsy Alpha", district "central" (founder request 2026-10-13, ADR-0015: "make
 * the city area with buildings larger. Need more streets."). Like v3 (ADR-0008, "a bigger city") it
 * ONLY ADDS: every v3 plot keeps its id, address, bounds, build bounds, entrance, door, signs and
 * anchors, and so do the spawn, the return anchors, the overview camera, the v3 interiors, the
 * movement settings and every billboard (fixtures/world-v3-snapshot.json; the v2 ones also against
 * fixtures/world-v2-snapshot.json).
 *
 * Layout (metres, x east, z north):
 *  - Main Street runs east-west along z = 0 and is 600 m long (x in [-300, 300]); road z in [-3, 3],
 *    4 m sidewalks on both sides. Traffic U-turns 6 m before each end.
 *  - Commercial plots line the NORTH side at a 14 m pitch (12 m plots, 2 m alleys), fronts facing
 *    south toward the plaza and the default overview camera: the v2 block c-01..c-10 (x -63..63),
 *    continued east by c-11..c-18 (x 77..175, "110".."180 Main Street") and west by c-19..c-26
 *    (x -77..-175, "10".."80 West Main Street"). Four more face NORTH from the south side, between
 *    Elm/Birch Lane and Cedar/Maple Lane (c-27..c-30, x +-124 / +-138). All start at height stage 0.
 *  - The spawn plaza sits SOUTH of the street, centred on x = 0, with a fountain; the civic shops
 *    (v-01..v-03) stand on the south side facing the street.
 *  - Four residential lanes run south from Main Street: Elm Lane (x = -70) and Birch Lane (x = 70)
 *    from v2, extended, plus Cedar Lane (x = -190) and Maple Lane (x = 190). Plots (20 x 20 m) line
 *    both sides in rows at z = -42, -66, -90, -114 and, south of the back road, -156.
 *  - Orchard Road, the back road (z = -135), joins all four lanes: 4-way junctions at Elm and Birch
 *    (the lanes continue south to a short cul-de-sac row) and T-junctions at Cedar and Maple.
 *  - World v4: Willow Avenue (x = -240) and Aspen Avenue (x = 240) run north from Main Street
 *    (T-junctions) to Market Street (z = 70, x -240..240), which meets both at corners. Market
 *    Street's south side has 20 shops facing north (c-31..c-50, x = -133..133, the Main Street
 *    columns); its north side has the four attractions (v-04..v-07, ./venues/sites.ts) and 13 shops
 *    facing south between them and at the ends (c-51..c-63). Fences, a back alley and trees fill the
 *    closed back lots between the two rows of shops; the district grows north to z = 150.
 *  - Billboards: bb-1 / bb-2 flank the plaza facing south; bb-3 / bb-4 close the two ends of Main
 *    Street, facing back along it.
 *
 * Property ids are stable and never depend on array order. Unowned residential plots are empty land
 * with a "For Sale" sign; an owned plot gets a home.
 *
 * World v5 (ADR-0041, founder 2026-10-23: "2 new districts", "Mixed like today"): three districts in one coordinate
 * frame, Central first, then Riverside (east, x 312..632) and Hillcrest (west, x -632..-312), each its own street room
 * (districts/shoppingDistrict.ts: 20 shops, 20 home plots, 2 billboards each). Central keeps everything of v4
 * (fixtures/world-v4-snapshot.json; worldV5.test.ts lists the deliberate differences): its only change is a short
 * pedestrian boulevard past each Market Street corner (z 64..76, x +-247..+-312) to a gate in its east / west
 * boundary wall, paired with the new district's gate; the closed back lots beyond the avenues are split around the
 * boulevards; the boulevard lamps and trees come after Central's v4 props, so every v4 prop index, prop collider id
 * and bench seat id (`bench:<propIndex>:<n>`) is unchanged. Surfaces, props and colliders added in v5 carry their
 * `districtId`; the v4 ones carry none and belong to Central (districtItems.ts). Use districtManifest(m, id)
 * (districtSlice.ts) for one district's street geometry; plot lookups keep the whole manifest.
 *
 * World v6 (ADR-0042, founder 2026-10-24: "They need to be attached to the town so you can drive there"; "One shared
 * town"): ONE continuous town, one street room built from the whole manifest. Market Street continues east and west
 * past the Aspen / Willow Avenue corners as a real two-way road with sidewalks and lamps through the former boulevards
 * to Central's edge (x +-312), where River Street (Riverside) and High Street (Hillcrest) carry it on, all at z = 70.
 * The corners are T-junctions now (crosswalks on all three arms). The walls between the districts are open for the
 * road and its sidewalks (z 63..77, LINK_CORRIDOR_Z) and closed everywhere else; the gates are gone (`gates: []`).
 * Every plot, billboard, interior, the movement settings and every district's id, name, bounds, square, spawn,
 * return anchors and overview camera are exactly as in v5 (fixtures/world-v5-snapshot.json, worldV6.test.ts). Only
 * roads, sidewalks, crosswalks, boundary colliders and street decor changed (Central: the boulevard paving, trees and
 * lamps became Market Street's stretch; the new districts: the gate forecourt and its lamps became the start of
 * their shopping street). Handed the whole manifest, the street-geometry functions (streetColliders, streetDecor,
 * streetSeats, drivableRects, trafficLoops) now mean the WHOLE TOWN; a district slice still means that district.
 *
 * Still world v6, ADR-0044 (founder 2026-10-24: "a new area attached to the town by road, all five stadiums together
 * as big landmarks"): a fourth district, the Sports Park (`sportspark`, districts/sportsPark.ts), north of Central with
 * the five civic stadiums v-08..v-12 on Stadium Way (venues/stadiums.ts) and their rooms (venues/arenas). Arena Avenue
 * carries Aspen Avenue's line on north across Market Street (a four-way junction now) through Central's back lot to
 * the park; Central's north wall and that back lot open for its corridor (x 233..247). Every existing plot, billboard,
 * interior, district and named street is unchanged (fixtures/world-v6-snapshot.json, sportsPark.test.ts).
 */
export const WORLD_VERSION = 6;
export const WORLD_ID = "townsy_alpha";
/** Central, the first district (every pre-v5 district reference meant it). */
export const DISTRICT_ID = "central";

export const MAIN_STREET_X_MIN = -300;
export const MAIN_STREET_X_MAX = 300;

const COMMERCIAL_CENTERS_X = [-63, -49, -35, -21, -7, 7, 21, 35, 49, 63] as const;
/** Facade style per plot (height is NOT part of the style: it comes from the acquisition stage). */
const COMMERCIAL_STYLE_ORDER: CommercialTemplateId[] = [
  "small_shop",
  "office_block",
  "colorful_tower",
  "corner_shop",
  "modern_midrise",
  "landmark_tower",
  "small_shop",
  "modern_midrise",
  "office_block",
  "corner_shop",
];
/** v3 north row east of c-10 (x = 77..175), west of c-01 (x = -77..-175) and the south-side shops. Neighbours never share a style. */
const EAST_ROW_STYLES: CommercialTemplateId[] = ["office_block", "small_shop", "modern_midrise", "corner_shop", "colorful_tower", "small_shop", "landmark_tower", "corner_shop"];
const WEST_ROW_STYLES: CommercialTemplateId[] = ["modern_midrise", "corner_shop", "office_block", "small_shop", "landmark_tower", "modern_midrise", "colorful_tower", "small_shop"];
const ROW_EXTENSION = 8; // plots per side
/** South-side shops (fronts facing north), between the inner and the outer lanes. */
const SOUTH_SHOPS: { cx: number; style: CommercialTemplateId }[] = [
  { cx: 124, style: "corner_shop" },
  { cx: 138, style: "modern_midrise" },
  { cx: -124, style: "small_shop" },
  { cx: -138, style: "office_block" },
];

const PLAZA: Aabb = aabb(-14, -29, 14, -ROAD_HALF - SIDEWALK); // south of the street
const FOUNTAIN_CENTER = { x: 0, z: -20 };

const OFFICE_CX = -50;
const OFFICE_FRONT_Z = -ROAD_HALF - SIDEWALK; // -7: south edge of the south sidewalk

// ---- Residential lanes --------------------------------------------------------------------------
/** Lanes, inner (v2) first: ids r-01..r-12 come from Elm and Birch exactly as in v2. */
export const LANES = [
  { x: -70, name: "Elm Lane" },
  { x: 70, name: "Birch Lane" },
  { x: -190, name: "Cedar Lane" },
  { x: 190, name: "Maple Lane" },
] as const;
const INNER_LANE_X = [-70, 70] as const;
const OUTER_LANE_X = [-190, 190] as const;
const RES_ROW_CENTERS_Z = [-42, -66, -90] as const; // v2 rows
/** v3 rows: one more north of the back road and a cul-de-sac row south of it. */
const RES_ROW_NORTH_NEW = -114;
const RES_ROW_SOUTH = -156;
/** Lane roads end 4 m past the last row (v2: -104 for the -90 row). */
const SIDE_Z_MIN = -170;

// ---- Back road (Orchard Road) -------------------------------------------------------------------
export const BACK_ROAD_NAME = "Orchard Road";
export const BACK_ROAD_Z = -135;
const BACK_ROAD: Aabb = aabb(-187, BACK_ROAD_Z - ROAD_HALF, 187, BACK_ROAD_Z + ROAD_HALF); // T at Cedar / Maple, through Elm / Birch

/** World v4 (ADR-0015) grows the district north (Market Street and the attractions); v3 ended at z = 40. */
const DISTRICT_BOUNDS: Aabb = aabb(-312, -178, 312, 150);

// ---- World v4: Market Street and the two avenues (ADR-0015) -------------------------------------
export const MARKET_STREET_NAME = "Market Street";
/** The avenues from Main Street up to Market Street, west first. */
export const AVENUES = [
  { x: WILLOW_AVENUE_X, name: "Willow Avenue" },
  { x: ASPEN_AVENUE_X, name: "Aspen Avenue" },
] as const;
/** Market Street's road: x -243..243 (the two corner squares belong to it), z 67..73. */
const MARKET_ROAD: Aabb = aabb(WILLOW_AVENUE_X - ROAD_HALF, MARKET_STREET_Z - ROAD_HALF, ASPEN_AVENUE_X + ROAD_HALF, MARKET_STREET_Z + ROAD_HALF);
/** Back line of the Main Street north row (z = 23) and of Market Street's south row (z = 47). */
const MAIN_NORTH_BACK_Z = COMMERCIAL_FRONT_Z + PLOT_D;
const MARKET_SOUTH_BACK_Z = MARKET_STREET_Z - COMMERCIAL_FRONT_Z - PLOT_D;
/** Back line of Market Street's north row (z = 93): behind it the land is closed (trees, the attractions' backs). */
const MARKET_NORTH_BACK_Z = MARKET_NORTH_FRONT_Z + PLOT_D;
/** Half width of an avenue corridor (road + sidewalks). */
const AVENUE_HALF = ROAD_HALF + SIDEWALK;
/**
 * World v6 (ADR-0042): Market Street's stretch from each avenue corner to Central's edge (x +-243..+-312), through the
 * closed back lots beyond the avenues (the v5 boulevards): the road (z 67..73) and its sidewalks (z 63..67, 73..77),
 * LINK_CORRIDOR_Z in all. The back lots stop at its sidewalks; the walls are open across it.
 */
const LINK = LINK_CORRIDOR_Z;
/** Where Market Street's v4 road (the corner squares included) ends and its v6 stretch begins: the avenues' outer road edge. */
const LINK_ROAD_X0 = ASPEN_AVENUE_X + ROAD_HALF; // 243
/** Market Street's south side: 20 shops facing north on the Main Street columns (x = +-7 .. +-133), west to east. */
const MARKET_SOUTH_X: number[] = [...Array.from({ length: 10 }, (_, k) => -133 + COMMERCIAL_PITCH * k), ...Array.from({ length: 10 }, (_, k) => 7 + COMMERCIAL_PITCH * k)];
const MARKET_SOUTH_STYLES: CommercialTemplateId[] = [
  "corner_shop",
  "modern_midrise",
  "small_shop",
  "landmark_tower",
  "office_block",
  "colorful_tower",
  "small_shop",
  "corner_shop",
  "modern_midrise",
  "office_block",
  "colorful_tower",
  "small_shop",
  "landmark_tower",
  "corner_shop",
  "office_block",
  "modern_midrise",
  "small_shop",
  "colorful_tower",
  "corner_shop",
  "landmark_tower",
];
/**
 * Market Street's north side between the attractions (60 m plots at x = -165, -55, 55, 165): three
 * shops in each 50 m gap and two at each end, 5 m clear of the attraction plots; west to east.
 */
const MARKET_NORTH_SHOPS: { cx: number; style: CommercialTemplateId }[] = [
  { cx: -220, style: "small_shop" },
  { cx: -206, style: "office_block" },
  { cx: -124, style: "modern_midrise" },
  { cx: -110, style: "corner_shop" },
  { cx: -96, style: "colorful_tower" },
  { cx: -14, style: "office_block" },
  { cx: 0, style: "landmark_tower" },
  { cx: 14, style: "small_shop" },
  { cx: 96, style: "colorful_tower" },
  { cx: 110, style: "small_shop" },
  { cx: 124, style: "modern_midrise" },
  { cx: 206, style: "corner_shop" },
  { cx: 220, style: "landmark_tower" },
];

/**
 * Market Street house numbers (ADR-0015): by position, two numbers per 14 m plot pitch counted from
 * the street's west end, odd on the north side and even on the south side, so numbers grow west to
 * east and facing places have close numbers (35 at x = 0 faces 34 and 36). The attractions carry the
 * numbers this gives (./venues/sites.ts lists them literally; worldV4.test.ts checks they agree), and
 * none of them takes a number another attraction had before the renumbering, so the seed's in-place
 * address updates never meet the unique address constraint halfway.
 */
export function marketStreetNumber(x: number, side: "north" | "south"): number {
  return 2 * Math.round((x - MARKET_STREET_X_MIN) / COMMERCIAL_PITCH) + (side === "north" ? 1 : 0);
}

/** House number of a south-side shop: odd, between its north-side neighbours across the street. */
function southShopAddress(cx: number): string {
  if (cx > 0) return `${oddFloor((10 * (cx + 77)) / COMMERCIAL_PITCH)} Main Street`;
  return `${oddFloor((10 * (-cx - 63)) / COMMERCIAL_PITCH)} West Main Street`;
}

function oddFloor(v: number): number {
  const f = Math.floor(v);
  return f % 2 === 1 ? f : f - 1;
}

function commercialPlots(): Plot[] {
  const plots: Plot[] = COMMERCIAL_CENTERS_X.map((cx, i) => commercialPlot(i + 1, cx, "north", COMMERCIAL_STYLE_ORDER[i]!, `${(i + 1) * 10} Main Street`));
  let n = COMMERCIAL_CENTERS_X.length;
  // c-11..c-18: east of c-10, "110 Main Street".."180 Main Street" (the v2 numbering continued).
  for (let k = 0; k < ROW_EXTENSION; k++) {
    const cx = 63 + COMMERCIAL_PITCH * (k + 1);
    plots.push(commercialPlot(++n, cx, "north", EAST_ROW_STYLES[k]!, `${(11 + k) * 10} Main Street`));
  }
  // c-19..c-26: west of c-01, "10 West Main Street".."80 West Main Street" (numbered outward).
  for (let k = 0; k < ROW_EXTENSION; k++) {
    const cx = -63 - COMMERCIAL_PITCH * (k + 1);
    plots.push(commercialPlot(++n, cx, "north", WEST_ROW_STYLES[k]!, `${(k + 1) * 10} West Main Street`));
  }
  // c-27..c-30: south side, fronts facing north.
  for (const s of SOUTH_SHOPS) plots.push(commercialPlot(++n, s.cx, "south", s.style, southShopAddress(s.cx)));
  // World v4 (ADR-0015). c-31..c-50: Market Street's south side, fronts facing north, west to east.
  MARKET_SOUTH_X.forEach((cx, k) => plots.push(commercialPlot(++n, cx, "south", MARKET_SOUTH_STYLES[k]!, `${marketStreetNumber(cx, "south")} ${MARKET_STREET_NAME}`, MARKET_STREET_Z)));
  // c-51..c-63: Market Street's north side between the attractions, fronts facing south, west to east.
  for (const s of MARKET_NORTH_SHOPS) plots.push(commercialPlot(++n, s.cx, "north", s.style, `${marketStreetNumber(s.cx, "north")} ${MARKET_STREET_NAME}`, MARKET_STREET_Z));
  return plots;
}

interface CivicSpec {
  id: string;
  cx: number;
  address: string;
  kind: "real_estate_office" | "home_builders" | "furnishings";
  name: string;
  description: string;
  interior: "office_room" | "showroom";
}

/** Townsy-run buildings on the south side of Main Street, facing the shops. Never purchasable. */
const CIVIC_BUILDINGS: CivicSpec[] = [
  {
    id: "v-01",
    cx: OFFICE_CX,
    address: "1 Plaza Way",
    kind: "real_estate_office",
    name: "Townsy Real Estate",
    description: "Buy a plot of land for your home here. Your plot starts with a tent; upgrade it at Townsy Home Builders.",
    interior: "office_room",
  },
  {
    id: "v-02",
    cx: 50,
    address: "2 Plaza Way",
    kind: "home_builders",
    name: "Townsy Home Builders",
    description: "Upgrade your home from tent to castle, and buy pools, swing sets, slides, trees, plants and fences for your yard.",
    interior: "showroom",
  },
  {
    id: "v-03",
    cx: 100,
    address: "3 Plaza Way",
    kind: "furnishings",
    name: "Townsy Furnishings",
    description: "Sofas, beds, rugs, lamps, plants and more to furnish the inside of your home.",
    interior: "showroom",
  },
];

function civicPlots(): Plot[] {
  const f = CIVIC_OFFICE.footprint;
  const frontZ = OFFICE_FRONT_Z;
  const wallZ = frontZ - 0.5; // -7.5
  const roofY = CIVIC_OFFICE.heightMetres + CIVIC_OFFICE.rooftopSign.height / 2 + 0.3;
  return CIVIC_BUILDINGS.map((c) => ({
    id: c.id,
    districtId: DISTRICT_ID,
    type: "CIVIC" as const,
    address: c.address,
    bounds: aabb(c.cx - 8, frontZ - 16, c.cx + 8, frontZ),
    buildBounds: aabb(c.cx - f.width / 2, wallZ - f.depth, c.cx + f.width / 2, wallZ),
    entrance: { position: { x: c.cx, z: wallZ + ENTRANCE_SETBACK }, facing: YAW_SOUTH },
    door: { position: { x: c.cx, z: wallZ }, direction: { x: 0, z: -1 }, halfWidth: 1.2 },
    facing: YAW_NORTH,
    civic: { kind: c.kind, name: c.name, description: c.description },
    interiorTemplate: c.interior,
    signAnchors: [
      {
        kind: "storefront" as const,
        position: { x: c.cx, y: STOREFRONT_SIGN_Y, z: wallZ + 0.06 },
        facing: YAW_NORTH,
        width: CIVIC_OFFICE.storefrontSign.width,
        height: CIVIC_OFFICE.storefrontSign.height,
      },
      {
        kind: "skyline" as const,
        position: { x: c.cx, y: roofY, z: wallZ - 1.2 },
        facing: YAW_NORTH,
        width: CIVIC_OFFICE.rooftopSign.width,
        height: CIVIC_OFFICE.rooftopSign.height,
      },
      // Back-to-back board facing the plaza / overview camera.
      {
        kind: "skyline" as const,
        position: { x: c.cx, y: roofY, z: wallZ - 1.5 },
        facing: YAW_SOUTH,
        width: CIVIC_OFFICE.rooftopSign.width,
        height: CIVIC_OFFICE.rooftopSign.height,
      },
    ],
    decorationAnchors: [],
    heightBand: 0,
  }));
}

function laneName(sx: number): string {
  return LANES.find((l) => l.x === sx)!.name;
}

/**
 * Residential plots:
 *  - r-01..r-12: the v2 plots (Elm then Birch; west then east; rows -42, -66, -90), "1".."12" with
 *    the v2 global numbering (1-6 Elm Lane, 7-12 Birch Lane);
 *  - r-13..r-20: Elm and Birch extended (rows -114 and -156 south of the back road), numbered on
 *    from v2: 7-10 Elm Lane, 13-16 Birch Lane;
 *  - r-21..r-40: Cedar Lane (r-21..r-30) and Maple Lane (r-31..r-40), five rows a side, "1".."10".
 */
function residentialPlots(): Plot[] {
  const plots: Plot[] = [];
  let n = 0;
  for (const sx of INNER_LANE_X) {
    for (const side of ["west", "east"] as const) {
      for (const cz of RES_ROW_CENTERS_Z) {
        n += 1;
        plots.push(residentialPlot(n, sx, side, cz, `${n} ${laneName(sx)}`));
      }
    }
  }
  const nextNumber: Record<number, number> = { [-70]: 7, 70: 13 };
  for (const sx of INNER_LANE_X) {
    for (const side of ["west", "east"] as const) {
      for (const cz of [RES_ROW_NORTH_NEW, RES_ROW_SOUTH]) {
        n += 1;
        const k = nextNumber[sx]!;
        nextNumber[sx] = k + 1;
        plots.push(residentialPlot(n, sx, side, cz, `${k} ${laneName(sx)}`));
      }
    }
  }
  for (const sx of OUTER_LANE_X) {
    let k = 0;
    for (const side of ["west", "east"] as const) {
      for (const cz of [...RES_ROW_CENTERS_Z, RES_ROW_NORTH_NEW, RES_ROW_SOUTH]) {
        n += 1;
        k += 1;
        plots.push(residentialPlot(n, sx, side, cz, `${k} ${laneName(sx)}`));
      }
    }
  }
  return plots;
}

function billboards(): Billboard[] {
  return [
    { id: "bb-1", districtId: DISTRICT_ID, position: { x: -27, y: 5, z: -9.5 }, facing: YAW_SOUTH, width: 10, height: 4 },
    { id: "bb-2", districtId: DISTRICT_ID, position: { x: 27, y: 5, z: -9.5 }, facing: YAW_SOUTH, width: 10, height: 4 },
    // v3: the two ends of Main Street, 6 m past the road, facing back along it (rentals come in M5).
    { id: "bb-3", districtId: DISTRICT_ID, position: { x: MAIN_STREET_X_MIN - 6, y: 5, z: 0 }, facing: YAW_EAST, width: 10, height: 4 },
    { id: "bb-4", districtId: DISTRICT_ID, position: { x: MAIN_STREET_X_MAX + 6, y: 5, z: 0 }, facing: YAW_WEST, width: 10, height: 4 },
  ];
}

/** Crosswalk across a north-south road (x0..x1): deeper than wide so its zebra bars run north-south. */
function crosswalkAcrossLane(sx: number, z0: number, z1: number): Surface {
  return { kind: "crosswalk", aabb: aabb(sx - SIDE_ROAD_HALF, z0, sx + SIDE_ROAD_HALF, z1) };
}

/** Crosswalk across the back road (6 m wide, as wide as deep, so its zebra bars run east-west). */
function crosswalkAcrossBackRoad(x0: number, x1: number): Surface {
  return { kind: "crosswalk", aabb: aabb(x0, BACK_ROAD.minZ, x1, BACK_ROAD.maxZ) };
}

/** Crosswalks reach 6.5 m into a lane from the back road's sidewalk line (zebra bars along the lane). */
const LANE_CROSSWALK_DEPTH = 6.5;

function surfaces(): Surface[] {
  const s: Surface[] = [];
  s.push({ kind: "road", aabb: aabb(MAIN_STREET_X_MIN, -ROAD_HALF, MAIN_STREET_X_MAX, ROAD_HALF) });
  s.push({ kind: "sidewalk", aabb: aabb(MAIN_STREET_X_MIN, ROAD_HALF, MAIN_STREET_X_MAX, ROAD_HALF + SIDEWALK) });
  s.push({ kind: "sidewalk", aabb: aabb(MAIN_STREET_X_MIN, -ROAD_HALF - SIDEWALK, MAIN_STREET_X_MAX, -ROAD_HALF) });
  s.push({ kind: "plaza", aabb: PLAZA });
  for (const { x: sx } of LANES) {
    const inner = (INNER_LANE_X as readonly number[]).includes(sx);
    if (inner) {
      // Elm and Birch cross the back road: two road pieces, the junction square belongs to the back road.
      s.push({ kind: "road", aabb: aabb(sx - SIDE_ROAD_HALF, BACK_ROAD.maxZ, sx + SIDE_ROAD_HALF, -ROAD_HALF) });
      s.push({ kind: "road", aabb: aabb(sx - SIDE_ROAD_HALF, SIDE_Z_MIN, sx + SIDE_ROAD_HALF, BACK_ROAD.minZ) });
    } else {
      // Cedar and Maple run through; the back road ends at them (T-junction).
      s.push({ kind: "road", aabb: aabb(sx - SIDE_ROAD_HALF, SIDE_Z_MIN, sx + SIDE_ROAD_HALF, -ROAD_HALF) });
    }
    s.push({ kind: "sidewalk", aabb: aabb(sx - SIDE_ROAD_HALF - SIDEWALK, SIDE_Z_MIN, sx - SIDE_ROAD_HALF, -ROAD_HALF - SIDEWALK) });
    s.push({ kind: "sidewalk", aabb: aabb(sx + SIDE_ROAD_HALF, SIDE_Z_MIN, sx + SIDE_ROAD_HALF + SIDEWALK, -ROAD_HALF - SIDEWALK) });
    s.push({ kind: "crosswalk", aabb: aabb(sx - SIDE_ROAD_HALF, -ROAD_HALF, sx + SIDE_ROAD_HALF, ROAD_HALF) });
  }
  s.push({ kind: "crosswalk", aabb: aabb(-3, -ROAD_HALF, 3, ROAD_HALF) });
  // Orchard Road (the back road) and its sidewalks between the lanes' sidewalks (no overlapping slabs).
  s.push({ kind: "road", aabb: BACK_ROAD });
  const laneSidewalkEdges = [-190, -70, 70, 190].map((x) => [x - SIDE_ROAD_HALF - SIDEWALK, x + SIDE_ROAD_HALF + SIDEWALK] as const);
  for (let i = 0; i + 1 < laneSidewalkEdges.length; i++) {
    const x0 = laneSidewalkEdges[i]![1];
    const x1 = laneSidewalkEdges[i + 1]![0];
    s.push({ kind: "sidewalk", aabb: aabb(x0, BACK_ROAD.maxZ, x1, BACK_ROAD.maxZ + SIDEWALK) });
    s.push({ kind: "sidewalk", aabb: aabb(x0, BACK_ROAD.minZ - SIDEWALK, x1, BACK_ROAD.minZ) });
  }
  // Junction crosswalks: across the lane on its north and south arms, across the back road on each side arm.
  for (const { x: sx } of LANES) {
    s.push(crosswalkAcrossLane(sx, BACK_ROAD.maxZ, BACK_ROAD.maxZ + LANE_CROSSWALK_DEPTH));
    s.push(crosswalkAcrossLane(sx, BACK_ROAD.minZ - LANE_CROSSWALK_DEPTH, BACK_ROAD.minZ));
    const w = 2 * ROAD_HALF;
    if (sx > -190) s.push(crosswalkAcrossBackRoad(sx - SIDE_ROAD_HALF - w, sx - SIDE_ROAD_HALF));
    if (sx < 190) s.push(crosswalkAcrossBackRoad(sx + SIDE_ROAD_HALF, sx + SIDE_ROAD_HALF + w));
  }
  s.push(...marketStreetSurfaces());
  return s;
}

/**
 * World v4 (ADR-0015) surfaces: Market Street (its road owns the two corner squares) and the avenues
 * from Main Street's north edge up to it, with 4 m sidewalks that never overlap (v4/v5: the outer sidewalks
 * ran round the corners; v6: they stop at Market Street's kerb, linkSurfaces continues the street; the inner ones
 * meet Market Street's south sidewalk). Crosswalks: across Main Street at each avenue (as at the lanes), across each
 * avenue mouth on Main Street's north sidewalk line and just south of each corner, across Market Street at each
 * corner (its inner side; v6 adds the outer side) and in front of each attraction's door.
 */
function marketStreetSurfaces(): Surface[] {
  const s: Surface[] = [];
  const north0 = MARKET_STREET_Z + ROAD_HALF; // 73
  const south0 = MARKET_STREET_Z - ROAD_HALF; // 67
  s.push({ kind: "road", aabb: MARKET_ROAD });
  for (const { x } of AVENUES) s.push({ kind: "road", aabb: aabb(x - ROAD_HALF, ROAD_HALF, x + ROAD_HALF, south0) });
  const inner = WILLOW_AVENUE_X + AVENUE_HALF; // -233: inner sidewalk edges of the avenues
  s.push({ kind: "sidewalk", aabb: aabb(inner, south0 - SIDEWALK, -inner, south0) });
  s.push({ kind: "sidewalk", aabb: aabb(WILLOW_AVENUE_X - AVENUE_HALF, north0, ASPEN_AVENUE_X + AVENUE_HALF, north0 + SIDEWALK) });
  for (const { x } of AVENUES) {
    const out = Math.sign(x); // away from the town centre
    const outerX0 = x + out * ROAD_HALF;
    const innerX0 = x - out * ROAD_HALF;
    // World v6: the outer sidewalk stops at Market Street's south kerb (v4/v5: it went round the corner to z 73), where
    // Market Street now continues past the corner (linkSurfaces).
    s.push({ kind: "sidewalk", aabb: aabb(Math.min(outerX0, outerX0 + out * SIDEWALK), COMMERCIAL_FRONT_Z, Math.max(outerX0, outerX0 + out * SIDEWALK), south0) });
    s.push({ kind: "sidewalk", aabb: aabb(Math.min(innerX0, innerX0 - out * SIDEWALK), COMMERCIAL_FRONT_Z, Math.max(innerX0, innerX0 - out * SIDEWALK), south0) });
  }
  for (const { x } of AVENUES) {
    s.push({ kind: "crosswalk", aabb: aabb(x - ROAD_HALF, -ROAD_HALF, x + ROAD_HALF, ROAD_HALF) });
    s.push(crosswalkAcrossLane(x, ROAD_HALF, ROAD_HALF + LANE_CROSSWALK_DEPTH));
    s.push(crosswalkAcrossLane(x, south0 - LANE_CROSSWALK_DEPTH, south0));
    const inward = -Math.sign(x);
    const x0 = x + inward * ROAD_HALF;
    s.push({ kind: "crosswalk", aabb: aabb(Math.min(x0, x0 + inward * 2 * ROAD_HALF), south0, Math.max(x0, x0 + inward * 2 * ROAD_HALF), north0) });
  }
  for (const v of VENUE_SITES) s.push({ kind: "crosswalk", aabb: aabb(v.cx - ROAD_HALF, south0, v.cx + ROAD_HALF, north0) });
  return s;
}

/** True when x is within `d` of one of the values. */
function near(x: number, values: readonly number[], d: number): boolean {
  return values.some((v) => Math.abs(x - v) <= d);
}

function props(): WorldProp[] {
  const p: WorldProp[] = [];
  const laneXs = LANES.map((l) => l.x);
  const southShopXs = SOUTH_SHOPS.map((s) => s.cx);
  const southTreeZ = -ROAD_HALF - SIDEWALK - 1.5;
  for (let k = 0; k <= 23; k++) {
    for (const sign of [-1, 1]) {
      const x = sign * (20 + 12 * k);
      if (Math.abs(x) > 290) continue;
      if (near(x, laneXs, 9)) continue; // lane junctions
      if (CIVIC_BUILDINGS.some((c) => Math.abs(x - c.cx) <= 10)) continue; // civic frontages
      // The square's billboards (10 m wide at x = +-27): half a board plus a crown, so no tree grows through the panel
      // (founder 2026-10-28: "why are the trees merging into the billboards?"; apps/web billboardTrees.test.ts).
      if (Math.abs(Math.abs(x) - 27) <= 10 / 2 + 2.5) continue;
      if (near(x, southShopXs, 7.5)) continue; // south-side shop plots
      p.push({ kind: "tree", position: { x, z: southTreeZ }, rotation: 0, scale: 1 });
    }
  }
  // Street lamps on Main Street every 28 m: north side in the alleys between shops (and on past the
  // row), south side clear of the plaza crosswalk, the junctions and the shop doors; one 4 m from
  // each end (the traffic U-turns 6 m from the end clear them).
  const northLamps: number[] = [];
  for (let x = -280; x <= 280; x += 28) northLamps.push(x);
  for (const x of [...northLamps, -296, 296].sort((a, b) => a - b)) {
    p.push({ kind: "lamp", position: { x, z: ROAD_HALF + 0.6 }, rotation: 0, scale: 1 });
  }
  const southLamps = [-296, -280, -252, -224, -168, -131, -112, -84, -28, 28, 56, 84, 112, 131, 168, 224, 252, 280, 296];
  for (const x of southLamps) {
    p.push({ kind: "lamp", position: { x, z: -ROAD_HALF - 0.6 }, rotation: 0, scale: 1 });
  }
  // Trees in the open lots past both ends of the north row (world v4: none in an avenue's corridor).
  for (let k = 0; k <= 8; k++) {
    for (const sign of [-1, 1]) {
      const x = sign * (190 + 12 * k);
      if (near(x, AVENUES.map((a) => a.x), AVENUE_HALF + 1)) continue;
      p.push({ kind: "tree", position: { x, z: k % 2 === 0 ? 12 : 18 }, rotation: 0, scale: 1.1 });
    }
  }
  p.push({ kind: "fountain", position: FOUNTAIN_CENTER, rotation: 0, scale: 1 });
  for (const [x, z] of [
    [-9, -13],
    [9, -13],
    [-9, -26],
    [9, -26],
  ] as const) {
    p.push({ kind: "bench", position: { x, z }, rotation: x < 0 ? YAW_EAST : YAW_WEST, scale: 1 });
    p.push({ kind: "planter", position: { x: x * 1.3, z: z + (z < -20 ? -1.5 : 1.5) }, rotation: 0, scale: 1 });
  }
  // Trees along the lanes: only in the gaps between plots and beside the junctions (never on a lawn,
  // never on an anchor, never in a U-turn swing).
  for (const sx of laneXs) {
    for (const z of [-14, -26, -54, -78, -102, -126, -144, -168]) {
      p.push({ kind: "tree", position: { x: sx - SIDE_ROAD_HALF - SIDEWALK - 1.5, z }, rotation: 0, scale: 0.9 });
      p.push({ kind: "tree", position: { x: sx + SIDE_ROAD_HALF + SIDEWALK + 1.5, z }, rotation: 0, scale: 0.9 });
    }
  }
  // Lane lamps on the sidewalk by the kerb, between rows (alternating sides).
  for (const sx of laneXs) {
    for (const [z, side] of [
      [-30, -1],
      [-54, 1],
      [-78, -1],
      [-102, 1],
      [-150, -1],
    ] as const) {
      p.push({ kind: "lamp", position: { x: sx + side * (SIDE_ROAD_HALF + 0.6), z }, rotation: 0, scale: 1 });
    }
  }
  // Orchard Road lamps (clear of the junctions and the U-turns at Cedar / Maple).
  for (const x of [-160, -130, -100, -45, -15, 15, 45, 100, 130, 160]) {
    p.push({ kind: "lamp", position: { x, z: BACK_ROAD.maxZ + 0.6 }, rotation: 0, scale: 1 });
    p.push({ kind: "lamp", position: { x, z: BACK_ROAD.minZ - 0.6 }, rotation: 0, scale: 1 });
  }
  // Park trees in the open blocks behind the plots (between the lanes).
  for (const z of [-50, -75, -100]) {
    for (const x of [-30, -10, 10, 30, -148, -130, -112, 112, 130, 148]) {
      p.push({ kind: "tree", position: { x, z }, rotation: 0, scale: 1.05 });
    }
  }
  p.push(...marketStreetProps());
  return p;
}

/**
 * World v4 (ADR-0015) street furniture: lamps by the kerb along both avenues (alternating sides) and
 * along Market Street (south side in the alleys of the shop row and on past it, north side in the
 * alleys and either side of each attraction's frontage), clear of every crosswalk and door; avenue
 * trees 1.5 m beyond the sidewalks (as on the lanes; the inner ones stand in the closed back lots);
 * and small parks of trees at both ends of Market Street's south side, past the shop row.
 */
function marketStreetProps(): WorldProp[] {
  const p: WorldProp[] = [];
  const lamp = (x: number, z: number) => p.push({ kind: "lamp", position: { x, z }, rotation: 0, scale: 1 });
  const tree = (x: number, z: number, scale: number) => p.push({ kind: "tree", position: { x, z }, rotation: 0, scale });
  for (const { x } of AVENUES) {
    const out = Math.sign(x);
    for (const [z, side] of [
      [16, out],
      [31, -out],
      [46, out],
      [56, -out],
    ] as const) {
      lamp(x + side * (ROAD_HALF + 0.6), z);
    }
    for (const z of [14, 22, 30, 38, 46, 54]) {
      tree(x - AVENUE_HALF - 1.5, z, 0.95);
      tree(x + AVENUE_HALF + 1.5, z, 0.95);
    }
  }
  const southZ = MARKET_STREET_Z - ROAD_HALF - 0.6;
  for (const x of [-224, -196, -140, -112, -84, -28, 0, 28, 84, 112, 140, 196, 224]) lamp(x, southZ);
  const northZ = MARKET_STREET_Z + ROAD_HALF + 0.6;
  for (const x of [-213, -185, -145, -117, -103, -75, -35, -7, 7, 35, 75, 103, 117, 145, 185, 213]) lamp(x, northZ);
  // Pocket parks: x 142..230 on both ends, two staggered rows between the sidewalk and the closed back lot.
  for (const sign of [-1, 1]) {
    for (let k = 0; k < 8; k++) {
      const x = sign * (146 + 11 * k);
      tree(x, k % 2 === 0 ? 51 : 57.5, 1.05);
    }
  }
  return p;
}

/**
 * World v6 (ADR-0042) surfaces of Market Street's two stretches beyond the avenue corners (west first): the road from the
 * avenue's outer road edge to Central's edge (x +-243..+-312, z 67..73), its sidewalks from the avenue's outer
 * sidewalk to the edge (x +-247..+-312, z 73..77 and 63..67), and a crosswalk across it on the corner's outer side
 * (x +-243..+-249), so each corner is a T-junction with a crosswalk on all three arms. Tagged with Central; listed AFTER
 * every v4 surface. Nothing overlaps another slab (the avenue's outer sidewalk stops at z 67 since v6).
 */
function linkSurfaces(): Surface[] {
  const out: Surface[] = [];
  const north0 = MARKET_STREET_Z + ROAD_HALF; // 73
  const south0 = MARKET_STREET_Z - ROAD_HALF; // 67
  const walkX0 = ASPEN_AVENUE_X + AVENUE_HALF; // 247
  const edge = DISTRICT_BOUNDS.maxX; // 312
  for (const sign of [-1, 1]) {
    const box = (x0: number, z0: number, x1: number, z1: number): Aabb => aabb(Math.min(sign * x0, sign * x1), z0, Math.max(sign * x0, sign * x1), z1);
    out.push({ kind: "road", aabb: box(LINK_ROAD_X0, south0, edge, north0), districtId: DISTRICT_ID });
    out.push({ kind: "sidewalk", aabb: box(walkX0, north0, edge, LINK.max), districtId: DISTRICT_ID });
    out.push({ kind: "sidewalk", aabb: box(walkX0, LINK.min, edge, south0), districtId: DISTRICT_ID });
    out.push({ kind: "crosswalk", aabb: box(LINK_ROAD_X0, south0, LINK_ROAD_X0 + 2 * ROAD_HALF, north0), districtId: DISTRICT_ID });
  }
  return out;
}

/**
 * World v6 street furniture of the two stretches: kerb lamps on both sides every 28 m (continuing Market Street's rows
 * and leading into River / High Street's), clear of the corner crosswalks, and a row of trees 1.5 m beyond each sidewalk
 * standing in the closed back lots (as along the avenues). Tagged with Central; listed after Central's v4 props, so
 * every v4 prop index, prop collider id and bench seat id (`bench:<propIndex>:<n>`) is unchanged.
 */
function linkProps(): WorldProp[] {
  const p: WorldProp[] = [];
  for (const sign of [-1, 1]) {
    for (const x of [256, 284, 308]) {
      for (const z of [MARKET_STREET_Z + ROAD_HALF + 0.6, MARKET_STREET_Z - ROAD_HALF - 0.6]) p.push({ kind: "lamp", position: { x: sign * x, z }, rotation: 0, scale: 1, districtId: DISTRICT_ID });
    }
    for (const x of [256, 270, 284, 298]) {
      for (const z of [LINK.max + 1.5, LINK.min - 1.5]) p.push({ kind: "tree", position: { x: sign * x, z }, rotation: 0, scale: 0.95, districtId: DISTRICT_ID });
    }
  }
  return p;
}

/**
 * World v6 colliders of Central, after the v4 ones: the closed back lots beyond the avenues NORTH of Market Street's
 * stretches (the v4 "bound:backlot:w" / ":e" stop at its south sidewalk) and the stretch props' colliders
 * (`prop:<i>:<kind>`, `i` continuing Central's prop list after its v4 props).
 */
function linkColliders(ps: readonly WorldProp[], firstIndex: number): StaticCollider[] {
  const B = DISTRICT_BOUNDS;
  return [
    { id: "bound:backlot:w:n", kind: "boundary", aabb: aabb(B.minX, LINK.max, WILLOW_AVENUE_X - AVENUE_HALF, MARKET_NORTH_BACK_Z), districtId: DISTRICT_ID },
    { id: "bound:backlot:e:n", kind: "boundary", aabb: aabb(ASPEN_AVENUE_X + AVENUE_HALF, LINK.max, B.maxX, MARKET_NORTH_BACK_Z), districtId: DISTRICT_ID },
    ...ps.flatMap((p, k) => propColliders(p, firstIndex + k, DISTRICT_ID)),
  ];
}

/**
 * Static colliders. Residential homes are NOT here: an unowned plot is open land. Owned homes are
 * added at runtime with homeCollider(plot) through streetColliders(manifest, ownedResidentialIds).
 * Billboard legs, hedges, signs, mailboxes and other street decor come from streetDecor() (also added
 * by streetColliders); the round-2 1.2 m box at the billboard centre (where nothing stands) is gone.
 */
function colliders(plots: Plot[], _bbs: Billboard[], ps: WorldProp[]): StaticCollider[] {
  const c: StaticCollider[] = [];
  for (const plot of plots) {
    if (plot.type === "RESIDENTIAL") continue;
    c.push({ id: `bld:${plot.id}`, kind: "building", aabb: plot.buildBounds });
  }
  ps.forEach((p, i) => c.push(...propColliders(p, i)));
  const B = DISTRICT_BOUNDS;
  const T = 2;
  // World v6 (ADR-0042): the west and east walls are open for Market Street and its sidewalks (LINK_CORRIDOR_Z,
  // z 63..77), where the town goes on into Hillcrest and Riverside: each is a south (":s") and a north (":n") piece.
  c.push({ id: "bound:w:s", kind: "boundary", aabb: aabb(B.minX - T, B.minZ - T, B.minX, LINK.min) });
  c.push({ id: "bound:w:n", kind: "boundary", aabb: aabb(B.minX - T, LINK.max, B.minX, B.maxZ + T) });
  c.push({ id: "bound:e:s", kind: "boundary", aabb: aabb(B.maxX, B.minZ - T, B.maxX + T, LINK.min) });
  c.push({ id: "bound:e:n", kind: "boundary", aabb: aabb(B.maxX, LINK.max, B.maxX + T, B.maxZ + T) });
  c.push({ id: "bound:s", kind: "boundary", aabb: aabb(B.minX - T, B.minZ - T, B.maxX + T, B.minZ) });
  // World v6 (ADR-0044): the north wall is open for Arena Avenue and its sidewalks (ARENA_AVENUE_CORRIDOR_X, x 233..247),
  // where the town goes on into the Sports Park: a west (":w") and an east (":e") piece.
  const arena = ARENA_AVENUE_CORRIDOR_X;
  c.push({ id: "bound:n:w", kind: "boundary", aabb: aabb(B.minX - T, B.maxZ, arena.min, B.maxZ + T) });
  c.push({ id: "bound:n:e", kind: "boundary", aabb: aabb(arena.max, B.maxZ, B.maxX + T, B.maxZ + T) });
  // Keep walkers off the commercial back lots (v3: behind the north row across the whole district
  // width; v2 left 10 m at each end open). World v4 (ADR-0015): between the Main Street north row and
  // Market Street's south row, open only where the avenues pass (road + sidewalks); beyond the
  // avenues up to Market Street's north row line; and everything behind Market Street's north row
  // (the attractions' backs). The renderer draws fences on the edges and trees and a back alley inside.
  // World v6 (ADR-0042): beyond the avenues the lots stop at Market Street's stretch to the town's other districts (v5:
  // at the boulevards to the gates); the pieces north of it are linkColliders'.
  const willowEdge = WILLOW_AVENUE_X + AVENUE_HALF;
  const aspenEdge = ASPEN_AVENUE_X - AVENUE_HALF;
  c.push({ id: "bound:backlot", kind: "boundary", aabb: aabb(willowEdge, MAIN_NORTH_BACK_Z, aspenEdge, MARKET_SOUTH_BACK_Z) });
  c.push({ id: "bound:backlot:w", kind: "boundary", aabb: aabb(B.minX, MAIN_NORTH_BACK_Z, WILLOW_AVENUE_X - AVENUE_HALF, LINK.min) });
  c.push({ id: "bound:backlot:e", kind: "boundary", aabb: aabb(ASPEN_AVENUE_X + AVENUE_HALF, MAIN_NORTH_BACK_Z, B.maxX, LINK.min) });
  // World v6 (ADR-0044): the lot behind Market Street's north row is open for Arena Avenue's corridor (x 233..247): the
  // v4 "bound:backlot:n" is its west part, "bound:backlot:n:e" its east part.
  c.push({ id: "bound:backlot:n", kind: "boundary", aabb: aabb(B.minX, MARKET_NORTH_BACK_Z, arena.min, B.maxZ) });
  c.push({ id: "bound:backlot:n:e", kind: "boundary", aabb: aabb(arena.max, MARKET_NORTH_BACK_Z, B.maxX, B.maxZ) });
  return c;
}

function interiors(): InteriorTemplate[] {
  const wall = (id: string, b: Aabb): StaticCollider => ({ id, kind: "building", aabb: b });
  return [
    {
      id: "store_room",
      bounds: aabb(-8, -6, 8, 6),
      spawn: { position: { x: 0, z: -3.6 }, facing: YAW_NORTH },
      exit: { position: { x: 0, z: -5 }, facing: YAW_SOUTH },
      exitDoor: { position: { x: 0, z: -6 }, direction: { x: 0, z: -1 }, halfWidth: 1.2 },
      signAnchor: { kind: "storefront", position: { x: 0, y: 2.6, z: 5.9 }, facing: YAW_SOUTH, width: 7, height: 2.2 },
      // Owner marketing board: a wall TV left of the brand sign.
      boardAnchor: { position: { x: -5.6, y: 2.1, z: 5.9 }, facing: YAW_SOUTH, width: 3.2, height: 1.9, style: "tv" },
      colliders: [wall("counter", aabb(-3, 2.2, 3, 3.4)), wall("shelf_w", aabb(-7.6, -2, -6.6, 3)), wall("shelf_e", aabb(6.6, -2, 7.6, 3))],
    },
    {
      id: "home_room",
      bounds: aabb(-6, -5, 6, 5),
      spawn: { position: { x: 0, z: -2.6 }, facing: YAW_NORTH },
      exit: { position: { x: 0, z: -4 }, facing: YAW_SOUTH },
      exitDoor: { position: { x: 0, z: -5 }, direction: { x: 0, z: -1 }, halfWidth: 1.1 },
      signAnchor: { kind: "storefront", position: { x: 0, y: 2.4, z: 4.9 }, facing: YAW_SOUTH, width: 4, height: 1.2 },
      // Unfurnished by default: owners furnish it with items from Townsy Furnishings (one room, even for a castle).
      decorationAnchors: [
        { id: "sofa_spot", type: "floor_large", position: { x: 3.9, z: -0.6 }, allowedRotations: [YAW_WEST, YAW_SOUTH] },
        { id: "bed_spot", type: "floor_large", position: { x: -3.7, z: 2.9 }, allowedRotations: [YAW_SOUTH, YAW_EAST] },
        { id: "table_spot", type: "table", position: { x: 0.6, z: 1.6 }, allowedRotations: [0, Math.PI / 2] },
        { id: "rug_center", type: "rug", position: { x: 0, z: -0.4 }, allowedRotations: [0, Math.PI / 2] },
        { id: "corner_ne", type: "floor_small", position: { x: 5.3, z: 4.3 }, allowedRotations: [YAW_SOUTH, YAW_WEST] },
        { id: "corner_nw", type: "floor_small", position: { x: -5.3, z: 0.6 }, allowedRotations: [YAW_EAST, YAW_SOUTH] },
        { id: "corner_se", type: "floor_small", position: { x: 5.3, z: -4.2 }, allowedRotations: [YAW_NORTH, YAW_WEST] },
        { id: "corner_sw", type: "floor_small", position: { x: -5.3, z: -4.2 }, allowedRotations: [YAW_NORTH, YAW_EAST] },
        // Beside the wall sign, clear of its frame board (x <= 2.2): the 1.62 m TV at 2.8 reached 0.21 m into the board
        // and its front shared the board's front plane, so the corner flickered (founder 2026-10-28).
        { id: "wall_north", type: "wall", position: { x: 3.2, z: 4.9 }, allowedRotations: [YAW_SOUTH] },
        { id: "wall_east", type: "wall", position: { x: 5.9, z: 1.6 }, allowedRotations: [YAW_WEST] },
        { id: "wall_west", type: "wall", position: { x: -5.9, z: -1.6 }, allowedRotations: [YAW_EAST] },
      ],
      colliders: [],
    },
    {
      id: "office_room",
      bounds: aabb(-7, -6, 7, 6),
      spawn: { position: { x: 0, z: -3.6 }, facing: YAW_NORTH },
      exit: { position: { x: 0, z: -5 }, facing: YAW_SOUTH },
      exitDoor: { position: { x: 0, z: -6 }, direction: { x: 0, z: -1 }, halfWidth: 1.2 },
      signAnchor: { kind: "storefront", position: { x: 0, y: 2.9, z: 5.9 }, facing: YAW_SOUTH, width: 6, height: 1.4 },
      // Plots-for-sale listings board on the east wall.
      boardAnchor: { position: { x: 6.9, y: 1.9, z: 1 }, facing: YAW_WEST, width: 4.4, height: 2.4, style: "listings" },
      colliders: [wall("desk", aabb(-2.5, 2, 2.5, 3.1)), wall("plant_w", aabb(-6.6, 4.6, -5.8, 5.4))],
    },
    {
      // Home Builders and Furnishings showrooms (catalog highlights board on the east wall; buying happens in the HTML panel).
      id: "showroom",
      bounds: aabb(-8, -6, 8, 6),
      spawn: { position: { x: 0, z: -3.6 }, facing: YAW_NORTH },
      exit: { position: { x: 0, z: -5 }, facing: YAW_SOUTH },
      exitDoor: { position: { x: 0, z: -6 }, direction: { x: 0, z: -1 }, halfWidth: 1.2 },
      signAnchor: { kind: "storefront", position: { x: 0, y: 2.9, z: 5.9 }, facing: YAW_SOUTH, width: 6, height: 1.4 },
      boardAnchor: { position: { x: 7.9, y: 1.9, z: 0.5 }, facing: YAW_WEST, width: 4.4, height: 2.4, style: "listings" },
      colliders: [
        wall("display_w", aabb(-5.5, 0, -2.5, 2.4)),
        wall("display_e", aabb(2.5, 0, 5.5, 2.4)),
        wall("counter", aabb(-2, 4, 2, 5)),
      ],
    },
    // Attractions (ADR-0016): one shared room per venue (layouts in ./venues).
    casinoTemplate(),
    raceTrackTemplate(),
    sportsCenterTemplate(),
    truckArenaTemplate(),
    // World v6 (ADR-0044): the five Sports Park stadium rooms (./venues/arenas), in property order v-08..v-12.
    ...STADIUM_SITES.map((s) => ARENA_INTERIORS[s.kind]),
  ];
}

function district(): District {
  return {
    id: DISTRICT_ID,
    name: "Central",
    bounds: DISTRICT_BOUNDS,
    spawn: { position: { x: 0, z: -11 }, facing: YAW_NORTH },
    returnAnchors: [
      { position: { x: -4, z: -11 }, facing: YAW_NORTH },
      { position: { x: 4, z: -11 }, facing: YAW_NORTH },
      { position: { x: 0, z: -15 }, facing: YAW_NORTH },
      { position: { x: -6, z: -16 }, facing: YAW_NORTH },
      { position: { x: 6, z: -16 }, facing: YAW_NORTH },
      { position: { x: 0, z: -8.5 }, facing: YAW_NORTH },
    ],
    overviewCamera: { target: { x: 0, y: 7, z: 6 }, alpha: -Math.PI / 2, beta: 1.08, radius: 92 },
    // World v5 (ADR-0041): the plaza is Central's square. World v6 (ADR-0042): no gates (Market Street goes on).
    square: PLAZA,
    gates: [],
  };
}

/**
 * Central as one district build: the v4 lists in their v4 order (v6: the avenues' outer sidewalks, the east / west walls
 * and the back lots beyond the avenues changed in place; ADR-0044: the north wall and the lot behind Market Street's
 * north row opened for Arena Avenue), then Market Street's two stretches to the town's other districts (world v6; v5 had
 * the boulevards there), then Arena Avenue's stretch up to the Sports Park (ADR-0044; its named street is the park's,
 * districts/sportsPark.ts).
 */
function centralDistrict(): DistrictBuild {
  const plots = [...commercialPlots(), ...civicPlots(), ...residentialPlots(), ...venuePlots(DISTRICT_ID)];
  const bbs = billboards();
  const v4Props = props();
  const link = linkProps();
  const arena = arenaAvenueInCentral(DISTRICT_BOUNDS.maxZ, v4Props.length + link.length, DISTRICT_ID);
  const ss = [...surfaces(), ...linkSurfaces()];
  return {
    district: district(),
    plots,
    billboards: bbs,
    surfaces: [...ss, ...arena.surfaces],
    props: [...v4Props, ...link, ...arena.props],
    colliders: [...colliders(plots, bbs, v4Props), ...linkColliders(link, v4Props.length), ...arena.colliders],
    streets: centralStreets(ss),
  };
}

function build() {
  // Central first (pre-v5 items are untagged and belong to the first district), then Riverside and Hillcrest, then
  // (world v6, ADR-0044) the Sports Park.
  const builds = [centralDistrict(), shoppingDistrict(RIVERSIDE), shoppingDistrict(HILLCREST), sportsPark()];
  const manifest = {
    version: WORLD_VERSION,
    worldId: WORLD_ID,
    districts: builds.map((b) => b.district),
    plots: builds.flatMap((b) => b.plots),
    billboards: builds.flatMap((b) => b.billboards),
    surfaces: builds.flatMap((b) => b.surfaces),
    props: builds.flatMap((b) => b.props),
    colliders: builds.flatMap((b) => b.colliders),
    interiors: interiors(),
    movement: {
      simulationHz: 20,
      patchHz: 15,
      walkSpeedMetresPerSecond: 3.5,
      actorRadiusMetres: 0.4,
      entranceRadiusMetres: 3,
      maxInputsPerTick: 3,
    },
  };
  return { manifest: WorldManifest.parse(manifest), streets: new Map(builds.map((b) => [b.district.id, b.streets] as const)) };
}

const BUILT = build();
export const WORLD_MANIFEST = BUILT.manifest;
export const COMMERCIAL_PLOT_PITCH = COMMERCIAL_PITCH;

/** A named street of a district (for the minimap, the directory and street labels). */
export interface StreetInfo {
  id: string;
  name: string;
  /**
   * "main" Main Street, "lane" a residential lane running south from it, "back" Orchard Road; world
   * v4: "avenue" Willow / Aspen Avenue running north from Main Street, "street" Market Street; world
   * v5: "street" also River Street / High Street (a district's shopping street), "lane" and "back"
   * their lanes and back roads. World v6: Market Street, River Street and High Street are one road at z = 70,
   * each named street the part of it in its own district. ADR-0044: "street" Stadium Way (the Sports Park's) and
   * "avenue" Arena Avenue, from Market Street's north kerb to Stadium Way: the park's street, its first stretch lying in
   * Central (the one road of Central's slice that is not one of Central's named streets).
   */
  kind: "main" | "lane" | "back" | "avenue" | "street";
  /** The road rectangles of the street (a lane crossed by the back road has two pieces; v6 Market Street three, west to east). */
  roads: Aabb[];
  /** Centre line from one end to the other (x/z metres). */
  from: { x: number; z: number };
  to: { x: number; z: number };
}

/**
 * Central's streets: Main Street, the four lanes (west to east) and Orchard Road, then (world v4)
 * Market Street, Willow Avenue and Aspen Avenue, from Central's road surfaces. Street names are the
 * ones used in plot addresses ("12 Elm Lane", "110 Main Street", "35 Market Street"; the west
 * extension of Main Street is addressed "10 West Main Street".. but it is the same road). The v3
 * streets keep their order (the directory ranks streets in this order). World v6: Market Street runs from Central's
 * west edge to its east edge (x -312..312: its stretch west of Willow Avenue, the v4 road, its stretch east of Aspen
 * Avenue); its house numbers are unchanged (marketStreetNumber counts from x = -240).
 */
function centralStreets(surfaces: readonly Surface[]): StreetInfo[] {
  const roads = surfaces.filter((x) => x.kind === "road").map((x) => x.aabb);
  const main = roads[0]!;
  const out: StreetInfo[] = [{ id: "main", name: "Main Street", kind: "main", roads: [main], from: { x: main.minX, z: 0 }, to: { x: main.maxX, z: 0 } }];
  for (const lane of [...LANES].sort((a, b) => a.x - b.x)) {
    const pieces = roads.filter((r) => r !== main && r !== BACK_ROAD && Math.abs((r.minX + r.maxX) / 2 - lane.x) < 1e-9);
    const minZ = Math.min(...pieces.map((r) => r.minZ));
    const maxZ = Math.max(...pieces.map((r) => r.maxZ));
    out.push({ id: lane.name.toLowerCase().replace(/\s+/g, "_"), name: lane.name, kind: "lane", roads: pieces, from: { x: lane.x, z: maxZ }, to: { x: lane.x, z: minZ } });
  }
  out.push({ id: "orchard_road", name: BACK_ROAD_NAME, kind: "back", roads: [BACK_ROAD], from: { x: BACK_ROAD.minX, z: BACK_ROAD_Z }, to: { x: BACK_ROAD.maxX, z: BACK_ROAD_Z } });
  // World v4: the manifest's own road rectangles (same values as MARKET_ROAD / the avenue roads).
  const same = (a: Aabb, b: Aabb) => a.minX === b.minX && a.minZ === b.minZ && a.maxX === b.maxX && a.maxZ === b.maxZ;
  const market = roads.find((r) => same(r, MARKET_ROAD))!;
  const linkW = roads.find((r) => same(r, aabb(-DISTRICT_BOUNDS.maxX, MARKET_ROAD.minZ, -LINK_ROAD_X0, MARKET_ROAD.maxZ)))!;
  const linkE = roads.find((r) => same(r, aabb(LINK_ROAD_X0, MARKET_ROAD.minZ, DISTRICT_BOUNDS.maxX, MARKET_ROAD.maxZ)))!;
  out.push({ id: "market_street", name: MARKET_STREET_NAME, kind: "street", roads: [linkW, market, linkE], from: { x: linkW.minX, z: MARKET_STREET_Z }, to: { x: linkE.maxX, z: MARKET_STREET_Z } });
  for (const avenue of AVENUES) {
    const road = roads.find((r) => same(r, aabb(avenue.x - ROAD_HALF, ROAD_HALF, avenue.x + ROAD_HALF, MARKET_ROAD.minZ)))!;
    out.push({ id: avenue.name.toLowerCase().replace(/\s+/g, "_"), name: avenue.name, kind: "avenue", roads: [road], from: { x: avenue.x, z: road.minZ }, to: { x: avenue.x, z: road.maxZ } });
  }
  return out;
}

/**
 * World v5 (ADR-0041): the named streets of every district of WORLD_MANIFEST (Central: centralStreets; Riverside and
 * Hillcrest: their shopping street, two lanes west to east and back road). Street ids are unique town-wide. Prefer
 * worldStreets(m, districtId) / districtManifest(m, id).streets (districtSlice.ts); townStreets (town.ts) lists them all.
 */
export const DISTRICT_STREETS: ReadonlyMap<string, readonly StreetInfo[]> = BUILT.streets;

/**
 * Central's streets (DISTRICT_STREETS "central"; before v5 the only district's). Consumers that mean "the streets of
 * the district I am in" should use worldStreets(m, districtId) (ADR-0041 slices 3 and 4 switch them).
 */
export const WORLD_STREETS: readonly StreetInfo[] = DISTRICT_STREETS.get(DISTRICT_ID)!;
